import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getCloudinaryUrl } from 'components/rich';

import PdpIcon from '../product/pdpIcon';

// Building blocks of the auth screens (docs/auth-flow.md). Styles: styles/_auth.scss (au- prefix).
// Values follow the "Auth 1-9" frames in design/website.pen.

export const MIN_PASSWORD_LENGTH = 6; // CredentialProvider::Photolane::MIN_PASSWORD_LENGTH

// hero/grid-9 has no width ladder on the CDN (800 px default, 1600 px @2x), same as hero/about.
const PHOTO_URL = getCloudinaryUrl({ imageKey: 'hero/grid-9' });
const PHOTO_SRC_SET = `${PHOTO_URL} 800w, ${PHOTO_URL.replace(/\.webp$/, '@2x.webp')} 1600w`;

export const AuthShell = ({ children }) => (
  <div className="au">
    <div className="au-column">
      <div className="au-form">{children}</div>
    </div>
    <div className="au-photo">
      <img src={PHOTO_URL} srcSet={PHOTO_SRC_SET} sizes="45vw" alt="" />
    </div>
  </div>
);

AuthShell.propTypes = {
  children: PropTypes.node,
};

export const Heading = ({ icon, tone, title, children }) => (
  <div className="au-heading">
    {icon && (
      <span className={classNames('au-badge', tone && `au-badge--${tone}`)}>
        <PdpIcon name={icon} size={26} />
      </span>
    )}
    {/* Focused after a mode switch so screen readers announce the new step. */}
    <h1 className="au-title" tabIndex={-1}>
      {title}
    </h1>
    {children && <p className="au-lead">{children}</p>}
  </div>
);

Heading.propTypes = {
  icon: PropTypes.string,
  tone: PropTypes.oneOf(['danger']),
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
};

export const BackLink = ({ onClick, children = 'Back to sign in' }) => (
  <button type="button" className="au-back" onClick={onClick}>
    <PdpIcon name="arrowLeft" size={18} />
    {children}
  </button>
);

BackLink.propTypes = {
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node,
};

// The aside (Forgot password?) comes after the input in the DOM so Tab goes from the email
// straight to the password. CSS places it next to the label.
export const Field = ({ id, label, aside, children }) => (
  <div className="au-field">
    <label className="au-label" htmlFor={id}>
      {label}
    </label>
    {children}
    {aside && <div className="au-field-aside">{aside}</div>}
  </div>
);

Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  aside: PropTypes.node,
  children: PropTypes.node,
};

export const EmailInput = ({ invalid, errorId, ...props }) => (
  <input
    type="email"
    autoComplete="email"
    inputMode="email"
    autoCapitalize="none"
    spellCheck={false}
    className={classNames('au-input', invalid && 'is-invalid')}
    aria-invalid={invalid || undefined}
    aria-describedby={invalid ? errorId : undefined}
    {...props}
  />
);

EmailInput.propTypes = {
  invalid: PropTypes.bool,
  errorId: PropTypes.string,
};

export const PasswordInput = ({ invalid, errorId, hintId, ...props }) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className={classNames('au-input au-input--password', invalid && 'is-invalid')}>
      <input
        type={visible ? 'text' : 'password'}
        aria-invalid={invalid || undefined}
        aria-describedby={[invalid && errorId, hintId].filter(Boolean).join(' ') || undefined}
        {...props}
        // Rollbar masks inputs only by type="password" or a name matching its scrubFields.
        // Show password switches the type to text, so the name keeps the value masked.
        name="password"
      />
      <button
        type="button"
        className="au-eye"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        onClick={() => setVisible((value) => !value)}
      >
        <PdpIcon name={visible ? 'eyeOff' : 'eye'} size={20} />
      </button>
    </div>
  );
};

PasswordInput.propTypes = {
  invalid: PropTypes.bool,
  errorId: PropTypes.string,
  hintId: PropTypes.string,
};

// Only shown where a new password is chosen. Sign in never checks the length.
export const PasswordHint = ({ id, password }) => (
  <p id={id} className={classNames('au-hint', password.length >= MIN_PASSWORD_LENGTH && 'is-met')}>
    <PdpIcon name="circleCheck" size={16} />
    At least {MIN_PASSWORD_LENGTH} characters
  </p>
);

PasswordHint.propTypes = {
  id: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
};

export const ErrorCallout = ({ id, title, children, actions }) => (
  <div id={id} className="au-callout" role="alert">
    <PdpIcon name="circleAlert" size={22} className="au-callout-icon" />
    <div className="au-callout-content">
      <p className="au-callout-title">{title}</p>
      {children && <p className="au-callout-body">{children}</p>}
      {actions && <div className="au-callout-actions">{actions}</div>}
    </div>
  </div>
);

ErrorCallout.propTypes = {
  id: PropTypes.string,
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
  actions: PropTypes.node,
};

export const CalloutAction = ({ onClick, arrow, children }) => (
  <button type="button" className="au-fix" onClick={onClick}>
    {children}
    {arrow && <PdpIcon name="arrowRight" size={16} />}
  </button>
);

CalloutAction.propTypes = {
  onClick: PropTypes.func.isRequired,
  arrow: PropTypes.bool,
  children: PropTypes.node,
};

export const Note = ({ icon, children }) => (
  <div className="au-note">
    <PdpIcon name={icon} size={18} />
    <p>{children}</p>
  </div>
);

Note.propTypes = {
  icon: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export const Divider = () => (
  <div className="au-divider" aria-hidden="true">
    <span>or</span>
  </div>
);
